/**
 * Notifications Data Layer — Phase 7.4
 * In-app notification system: create, fetch, mark-read, event reminders
 */

import { createClient } from '@/lib/supabase/client';

export type NotificationType =
  | 'application_approved' |'application_rejected' |'event_reminder' |'event_cancelled' |'session_reminder' |'attendance_checked_in';

export type NotificationStatus = 'unread' | 'read';
export type NotificationChannel = 'in_app' | 'email' | 'wechat';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  status: NotificationStatus;
  channel: NotificationChannel;
  related_event_id: string | null;
  related_session_id: string | null;
  created_at: string;
  read_at: string | null;
}

// ─── Fetch notifications for current user ────────────────────────────────────
export async function fetchMyNotifications(limit = 50): Promise<Notification[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[notifications] fetchMyNotifications error:', error.message);
    return [];
  }
  return (data || []) as Notification[];
}

// ─── Fetch all notifications (admin) ─────────────────────────────────────────
export async function fetchAllNotifications(limit = 200): Promise<(Notification & { user_profiles?: { display_name: string; email: string } })[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('notifications')
    .select('*, user_profiles(display_name, email)')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[notifications] fetchAllNotifications error:', error.message);
    return [];
  }
  return (data || []) as any[];
}

// ─── Get unread count for current user ───────────────────────────────────────
export async function getUnreadCount(): Promise<number> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('get_unread_notification_count');
  if (error) {
    console.error('[notifications] getUnreadCount error:', error.message);
    return 0;
  }
  return (data as number) || 0;
}

// ─── Mark a single notification as read ──────────────────────────────────────
export async function markNotificationRead(notificationId: string): Promise<boolean> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('mark_notification_read', {
    p_notification_id: notificationId,
  });
  if (error) {
    console.error('[notifications] markNotificationRead error:', error.message);
    return false;
  }
  return !!data;
}

// ─── Mark all notifications as read ──────────────────────────────────────────
export async function markAllNotificationsRead(): Promise<number> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('mark_all_notifications_read');
  if (error) {
    console.error('[notifications] markAllNotificationsRead error:', error.message);
    return 0;
  }
  return (data as number) || 0;
}

// ─── Create a notification (admin/coach only) ─────────────────────────────────
export async function createNotification(params: {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  channel?: NotificationChannel;
  relatedEventId?: string | null;
  relatedSessionId?: string | null;
}): Promise<string | null> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('create_notification', {
    p_user_id: params.userId,
    p_type: params.type,
    p_title: params.title,
    p_message: params.message,
    p_channel: params.channel || 'in_app',
    p_related_event_id: params.relatedEventId || null,
    p_related_session_id: params.relatedSessionId || null,
  });
  if (error) {
    console.error('[notifications] createNotification error:', error.message);
    return null;
  }
  return data as string;
}

// ─── Create application approved notification ─────────────────────────────────
export async function createApplicationApprovedNotification(userId: string): Promise<string | null> {
  return createNotification({
    userId,
    type: 'application_approved',
    title: 'Welcome to R3SET Running!',
    message: 'Your R3SET Running membership has been approved. Welcome to the community!',
    channel: 'in_app',
  });
}

// ─── Create application rejected notification ─────────────────────────────────
export async function createApplicationRejectedNotification(userId: string): Promise<string | null> {
  return createNotification({
    userId,
    type: 'application_rejected',
    title: 'Application Update',
    message: 'Thank you for your interest in R3SET Running. Unfortunately, we are unable to approve your application at this time.',
    channel: 'in_app',
  });
}

// ─── Create event reminder notifications (backend-ready) ─────────────────────
export async function createEventReminderNotifications(eventId: string): Promise<number> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('create_event_reminder_notifications', {
    p_event_id: eventId,
  });
  if (error) {
    console.error('[notifications] createEventReminderNotifications error:', error.message);
    return 0;
  }
  return (data as number) || 0;
}

// ─── Create event cancelled notifications (backend-ready) ────────────────────
export async function createEventCancelledNotifications(eventId: string): Promise<number> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('create_event_cancelled_notifications', {
    p_event_id: eventId,
  });
  if (error) {
    console.error('[notifications] createEventCancelledNotifications error:', error.message);
    return 0;
  }
  return (data as number) || 0;
}

// ─── Notification type display helpers ───────────────────────────────────────
export function getNotificationIcon(type: NotificationType): string {
  const icons: Record<NotificationType, string> = {
    application_approved: '✅',
    application_rejected: '❌',
    event_reminder: '📅',
    event_cancelled: '🚫',
    session_reminder: '🏃',
    attendance_checked_in: '✓',
  };
  return icons[type] || '🔔';
}

export function getNotificationTypeLabel(type: NotificationType): string {
  const labels: Record<NotificationType, string> = {
    application_approved: 'Application Approved',
    application_rejected: 'Application Rejected',
    event_reminder: 'Event Reminder',
    event_cancelled: 'Event Cancelled',
    session_reminder: 'Session Reminder',
    attendance_checked_in: 'Checked In',
  };
  return labels[type] || type;
}
